import React, { useState, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ShieldAlert, CheckCircle2, XCircle, Loader2, Trash2,
    Shield, Mail
} from "lucide-react";
import FemcareLogo from "../components/FemcareLogo";
import { api } from "../lib/api";
import { useReveal, useSpotlight } from "../lib/motion";

export default function DeleteAccountVerify() {
    const [params] = useSearchParams();
    const token = useMemo(() => (params.get("token") || "").trim(), [params]);

    const heroRef = useRef(null);
    useSpotlight(heroRef);
    useReveal();

    const [state, setState] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const noToken = token.length === 0;

    const onConfirm = async () => {
        setState("loading");
        setErrorMsg("");
        try {
            const { data } = await api.post("/account/delete-request/verify", { token });
            setState(data?.already ? "already" : "success");
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 404) {
                setState("invalid");
            } else if (status === 410) {
                setState("expired");
            } else if (status === 429) {
                setState("error");
                setErrorMsg("Too many attempts. Please try again in a bit.");
            } else {
                setState("error");
                setErrorMsg(typeof detail === "string" ? detail : "We couldn't verify your request. Please try again.");
            }
        }
    };

    return (
        <div className="bg-femcare-cream min-h-screen" data-testid="delete-verify-page">
            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden spotlight">
                <div className="aurora grain" aria-hidden="true" />
                <div className="max-w-2xl mx-auto px-6 lg:px-12 pt-24 pb-14 relative">
                    <div className="flex items-center gap-4 mb-10 reveal">
                        <div className="f-coin">
                            <FemcareLogo className="w-9 h-9" />
                        </div>
                        <span className="text-xs uppercase tracking-[0.35em] text-femcare-accent font-medium">
                            Confirm deletion
                        </span>
                    </div>
                    <h1
                        className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-femcare-purple leading-none reveal reveal-delay-1"
                        data-testid="delete-verify-heading"
                    >
                        Confirm your<br />
                        <span className="italic font-light">Femcare</span> account deletion.
                    </h1>
                </div>
            </section>

            <div className="max-w-2xl mx-auto px-6 lg:px-12 pb-24 -mt-4 relative z-10">
                <div className="card-editorial p-6 md:p-8 reveal reveal-delay-2">
                    {noToken ? (
                        <div className="flex gap-4 items-start" data-testid="delete-verify-invalid">
                            <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                            <div>
                                <h2 className="font-serif text-2xl text-femcare-purple mb-1">
                                    Missing verification token
                                </h2>
                                <p className="text-femcare-purple/75 leading-relaxed">
                                    This page needs a verification link from your email. Please open the
                                    &ldquo;Confirm your Femcare account deletion&rdquo; email we sent you and tap the
                                    button inside.
                                </p>
                            </div>
                        </div>
                    ) : state === "success" || state === "already" ? (
                        <div className="flex gap-4 items-start" data-testid="delete-verify-success">
                            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <h2 className="font-serif text-2xl text-femcare-purple mb-1">
                                    {state === "already"
                                        ? "Already confirmed"
                                        : "Deletion request confirmed"}
                                </h2>
                                <p className="text-femcare-purple/80 leading-relaxed">
                                    Thanks — your ownership of the account has been confirmed. Our team will
                                    now process the permanent deletion of your Femcare account and associated
                                    personal data. You&apos;ll receive an email once the deletion is complete.
                                </p>
                                <p className="mt-3 text-sm text-femcare-purple/60">
                                    Changed your mind? Email{" "}
                                    <a
                                        href="mailto:support@femcare.life"
                                        className="text-femcare-pink underline decoration-femcare-pink/30 hover:decoration-femcare-pink underline-offset-4"
                                    >
                                        support@femcare.life
                                    </a>{" "}
                                    right away to cancel.
                                </p>
                            </div>
                        </div>
                    ) : state === "invalid" ? (
                        <div className="flex gap-4 items-start" data-testid="delete-verify-invalid">
                            <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                            <div>
                                <h2 className="font-serif text-2xl text-femcare-purple mb-1">
                                    Link is invalid or already used
                                </h2>
                                <p className="text-femcare-purple/75 leading-relaxed">
                                    This verification link is no longer valid. If you still want to delete
                                    your account, please submit a fresh request.
                                </p>
                                <Link
                                    to="/delete-account"
                                    className="inline-flex items-center gap-2 mt-4 bg-femcare-pink hover:bg-femcare-pink/90 text-white rounded-full px-6 py-2.5 text-sm font-medium magnet"
                                    data-testid="delete-verify-restart"
                                >
                                    Start a new request
                                </Link>
                            </div>
                        </div>
                    ) : state === "expired" ? (
                        <div className="flex gap-4 items-start" data-testid="delete-verify-expired">
                            <XCircle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
                            <div>
                                <h2 className="font-serif text-2xl text-femcare-purple mb-1">
                                    This link has expired
                                </h2>
                                <p className="text-femcare-purple/75 leading-relaxed">
                                    Verification links are valid for 7 days. Please submit a new deletion
                                    request to receive a fresh link.
                                </p>
                                <Link
                                    to="/delete-account"
                                    className="inline-flex items-center gap-2 mt-4 bg-femcare-pink hover:bg-femcare-pink/90 text-white rounded-full px-6 py-2.5 text-sm font-medium magnet"
                                    data-testid="delete-verify-restart"
                                >
                                    Start a new request
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-3">
                                <span className="w-10 h-10 rounded-full bg-femcare-petal flex items-center justify-center">
                                    <ShieldAlert className="w-4 h-4 text-femcare-pink" />
                                </span>
                                <h2 className="font-serif text-2xl text-femcare-purple">
                                    Final confirmation
                                </h2>
                            </div>
                            <p className="text-femcare-purple/80 leading-relaxed">
                                By tapping the button below, you confirm that you are the owner of this
                                Femcare account and that you want your account and associated personal
                                data permanently deleted. This action <b>cannot be undone</b>.
                            </p>

                            {state === "error" && errorMsg && (
                                <div
                                    role="alert"
                                    className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm p-3"
                                    data-testid="delete-verify-error"
                                >
                                    {errorMsg}
                                </div>
                            )}

                            <button
                                onClick={onConfirm}
                                disabled={state === "loading"}
                                className="inline-flex items-center justify-center gap-2 mt-6 w-full sm:w-auto bg-femcare-pink hover:bg-femcare-pink/90 disabled:bg-femcare-pink/40 disabled:cursor-not-allowed text-white rounded-full px-7 py-3.5 font-medium magnet"
                                data-testid="delete-verify-confirm-btn"
                            >
                                {state === "loading" ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" /> Confirming…
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" /> Yes, delete my account
                                    </>
                                )}
                            </button>

                            <p className="mt-4 text-xs text-femcare-purple/55 leading-relaxed">
                                Didn&apos;t make this request? You can safely close this page — nothing will
                                happen. To be extra safe, change the password of the email account you use
                                for Femcare.
                            </p>
                        </>
                    )}
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm text-femcare-purple/70">
                    <Link
                        to="/privacy"
                        className="inline-flex items-center gap-1.5 hover:text-femcare-pink"
                    >
                        <Shield className="w-3.5 h-3.5" /> Privacy Policy
                    </Link>
                    <span className="text-femcare-purple/20">·</span>
                    <a
                        href="mailto:support@femcare.life"
                        className="inline-flex items-center gap-1.5 hover:text-femcare-pink"
                    >
                        <Mail className="w-3.5 h-3.5" /> support@femcare.life
                    </a>
                </div>
            </div>
        </div>
    );
}
